import { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MetricCard from "../components/MetricCard";
import DateRangePicker, { DateRange } from "../components/DateRangePicker";
import { getStatsUsers, getStatsCohorts } from "../api/adminApi";
import type { StatsUsersResponse, StatsCohortsResponse } from "../types/admin";

/**
 * Get default date range (last 30 days)
 */
function getDefaultDateRange(): DateRange {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);

  return {
    startDate: thirtyDaysAgo.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
    label: "Last 30 Days",
  };
}

function UserGrowth() {
  const [usersData, setUsersData] = useState<StatsUsersResponse | null>(null);
  const [cohortsData, setCohortsData] = useState<StatsCohortsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const [users, cohorts] = await Promise.all([
        getStatsUsers(dateRange.startDate, dateRange.endDate),
        getStatsCohorts(),
      ]);
      setUsersData(users);
      setCohortsData(cohorts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!usersData) return null;

  // Filter out days before any users existed (no meaningful data)
  // Keep days with total=0 if there was activity (new/churned users), or if it's after first user
  const firstUserDate = usersData.metrics
    .slice()
    .reverse()
    .find((day) => day.total > 0 || day.new > 0)?.date;
  
  const filteredMetrics = usersData.metrics.filter((day) => {
    // Always show if there are users
    if (day.total > 0) return true;
    // Show if there was activity (signups, churn, activity)
    if (day.new > 0 || day.churned > 0 || day.active > 0) return true;
    // Don't show empty days before any users existed
    return firstUserDate && day.date >= firstUserDate;
  });

  // Reverse for chronological order in charts
  const chartData = [...filteredMetrics].reverse();

  // Calculate totals from filtered metrics
  const latestDay = filteredMetrics[0];
  const previousDay = filteredMetrics[1];
  const growthRate = previousDay?.total
    ? ((latestDay.total - previousDay.total) / previousDay.total) * 100
    : 0;

  const totalNew = filteredMetrics.reduce((sum, day) => sum + day.new, 0);
  const totalChurned = filteredMetrics.reduce((sum, day) => sum + day.churned, 0);
  const totalDeleted = filteredMetrics.reduce((sum, day) => sum + (day.accountsDeleted || 0), 0);
  const netGrowth = totalNew - totalChurned - totalDeleted;

  return (
    <div>
      <header className="page-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div>
            <h1 className="page-title">User Growth</h1>
            <p className="page-subtitle">
              {usersData.period.start} to {usersData.period.end}
            </p>
          </div>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            minDate="2025-01-01"
          />
        </div>
      </header>

      {/* Summary Metrics */}
      <div className="metrics-grid">
        <MetricCard
          label="Total Users"
          value={latestDay?.total || 0}
          change={growthRate !== 0 ? { value: growthRate, label: "vs yesterday" } : undefined}
        />
        <MetricCard label="New Users" value={totalNew} />
        <MetricCard
          label="Churned Users"
          value={totalChurned}
          emphasis={totalChurned > 0 ? "warning" : "default"}
        />
        <MetricCard
          label="Accounts Deleted"
          value={totalDeleted}
          emphasis={totalDeleted > 0 ? "danger" : "default"}
        />
        <MetricCard
          label="Net Growth"
          value={netGrowth}
          emphasis={netGrowth < 0 ? "danger" : netGrowth > 0 ? "success" : "default"}
        />
      </div>

      {/* User Growth Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">User Growth Over Time</h3>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  tickFormatter={(value) => value?.slice(5) || ""}
                />
                <YAxis stroke="#94a3b8" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #475569",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#f1f5f9" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total Users"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="new"
                  name="New"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="active"
                  name="Active"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Cohort Analysis */}
      {cohortsData && cohortsData.cohortCount > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Cohort Retention Analysis</h3>
            <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              {cohortsData.cohortCount} cohorts
            </span>
          </div>
          <div className="card-body">
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cohort Week</th>
                    <th>Week 0</th>
                    <th>Week 1</th>
                    <th>Week 2</th>
                    <th>Week 3</th>
                    <th>Week 4</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(cohortsData.cohorts)
                    .slice(0, 10)
                    .map(([week, data]) => (
                      <tr key={week}>
                        <td>{week}</td>
                        {[0, 1, 2, 3, 4].map((weekNum) => {
                          const weekData = data.find((d) => d.weeksSinceSignup === weekNum);
                          const rate = weekData?.retentionRate;
                          return (
                            <td key={weekNum}>
                              {rate != null ? (
                                <span
                                  style={{
                                    color:
                                      rate >= 0.5
                                        ? "var(--accent-success)"
                                        : rate >= 0.25
                                          ? "var(--accent-warning)"
                                          : "var(--accent-danger)",
                                  }}
                                >
                                  {(rate * 100).toFixed(0)}%
                                </span>
                              ) : (
                                <span style={{ color: "var(--text-muted)" }}>-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Daily Details Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Daily Breakdown</h3>
          <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
            {filteredMetrics.length} days with data
          </span>
        </div>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Total</th>
                <th>New</th>
                <th>Active</th>
                <th title="Subscription cancellations (not account deletions)">
                  Churned*
                </th>
                <th title="Permanent account deletions (GDPR)">
                  Deleted**
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.map((day) => (
                <tr key={day.date}>
                  <td>{day.date}</td>
                  <td>{day.total.toLocaleString()}</td>
                  <td style={{ color: "var(--accent-success)" }}>+{day.new}</td>
                  <td>{day.active.toLocaleString()}</td>
                  <td style={{ color: day.churned > 0 ? "var(--accent-danger)" : "inherit" }}>
                    {day.churned > 0 ? `-${day.churned}` : day.churned}
                  </td>
                  <td style={{ color: (day.accountsDeleted || 0) > 0 ? "var(--accent-danger)" : "inherit" }}>
                    {(day.accountsDeleted || 0) > 0 ? `-${day.accountsDeleted}` : (day.accountsDeleted || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{
            color: "var(--text-muted)",
            fontSize: "0.75rem",
            marginTop: "0.75rem",
            fontStyle: "italic"
          }}>
            *Churned = subscription cancellations. **Deleted = permanent account deletions (GDPR Article 17).
          </p>
        </div>
      </div>
    </div>
  );
}

export default UserGrowth;
